import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { API_BASE } from './api'
import type { EventDetail, EventSummary } from '@/types'

/**
 * Events and their parking from commerce-api's public routes (logbook decision 109), through the
 * worker's /api/* as the rest of the map's data: /api/commerce/events → api.parkeze.com/v1/commerce/events.
 * In development only, `?events=mock` answers from a Columbia stand-in instead (./events-mock).
 */
const MOCK = import.meta.env.DEV && new URLSearchParams(window.location.search).get('events') === 'mock'

async function commerce<T>(path: string): Promise<T> {
  if (MOCK) return (await import('./events-mock')).answer(path) as T
  const res = await fetch(`${API_BASE}/commerce${path}`)
  if (!res.ok) throw new Error(`Events: ${res.status}`)
  return res.json()
}

/** How far ahead the map shows events. */
export const DAYS_AHEAD = 14

type Bbox = { west: number; south: number; east: number; north: number }

/** Events whose venue is in view over the coming days, soonest first. */
export function useEventsInView(bbox: Bbox | null, enabled: boolean) {
  // Rounded outward to about a kilometre, and `until` to the hour, so a small pan asks nothing new
  // and the worker's cache answers the same box for everyone.
  const box = bbox && [
    Math.floor(bbox.west * 100) / 100, Math.floor(bbox.south * 100) / 100,
    Math.ceil(bbox.east * 100) / 100, Math.ceil(bbox.north * 100) / 100,
  ].join(',')
  const until = new Date(Math.ceil(Date.now() / 3.6e6) * 3.6e6 + DAYS_AHEAD * 864e5).toISOString()
  return useQuery({
    queryKey: ['events', box, until],
    queryFn: async () => {
      const q = new URLSearchParams({ bbox: box!, until, limit: '100' })
      const { events } = await commerce<{ events: EventSummary[] }>(`/events?${q}`)
      return [...events].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
    },
    enabled: enabled && !!box,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

/** One event with its lots, prices and what is left, refreshed each minute while it is open. */
export function useEvent(id: string | null) {
  return useQuery({
    queryKey: ['event', id],
    queryFn: () => commerce<EventDetail>(`/events/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    retry: false,
  })
}
